// src/components/team/TeamSmartActions.jsx
//
// "Assistant coach" surface for the team Overview. Shows actionable
// prompts for the team's coach: registration about to close, unpaid
// registrations to chase, missing documents to follow up on. Each
// action has a primary CTA (lime) that takes the coach to the most
// efficient next step.
//
// This component takes its inputs as PROPS (no internal data fetching)
// because the consumer (ViewTeam.jsx) already loads activeSeason +
// registration stats — re-fetching here would duplicate queries and
// burn snapshot listeners. The hook-based equivalent (useTeamSmartActions)
// can be added later if a second consumer (e.g. cross-team Today panel)
// needs the same logic.
//
// Actions are ordered by urgency: closing reg > lineup-due >
// no-location (weather watch gap) > unpaid > missing docs > recap >
// disengaged-parent (stubbed pending parentUid on registrations).
// v1 (May 2026) shipped closing reg / unpaid / missing docs / recap;
// lineup-due, no-location, and disengaged-parent were layered in
// after.
//
// Renders null when there are zero actions — the coach sees nothing
// rather than a "you're all caught up!" empty state, since silence is
// less noisy than another card to dismiss.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection, query, where, orderBy, limit, onSnapshot, getDoc, getDocs, doc, documentId,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import clsx from 'clsx';
import RecommendToDirectorCard from './RecommendToDirectorCard';
import {
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  ArrowRightIcon,
  BellAlertIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

export default function TeamSmartActions({
  teamId,
  activeSeason,
  activeRegCount,
  unpaidCount,
  missingDocsCount,
  daysUntilClose,
}) {
  const { t } = useTranslation();

  // Recently-completed game (for the "draft a recap" prompt). Subscribes
  // to the team's games collection and keeps the most recent completed
  // game from the last 5 days that ALSO has a recorded score and has
  // NOT yet had a recap broadcast sent for it. The lookback was widened
  // from 48h → 5 days so weekend games still nudge the coach mid-week
  // before momentum fades. We cap at one prompt per team to keep the
  // smart-actions stack focused.
  const [recentGame, setRecentGame] = useState(null);
  useEffect(() => {
    if (!teamId) return;
    const cutoff = new Date(Date.now() - 5 * 24 * 60 * 60 * 1000); // 5-day window
    const q = query(
      collection(db, 'games'),
      where('teamId', '==', teamId),
      where('eventType', '==', 'game'),
      orderBy('date', 'desc'),
      limit(5),
    );
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const now = Date.now();
        // Filter to past games inside the lookback window that:
        //  - aren't cancelled
        //  - have a final score recorded (status==='completed' OR both
        //    score fields are non-null) — no point recapping an
        //    unplayed/abandoned game.
        const candidates = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((g) => {
            const ms = g.date?.toMillis ? g.date.toMillis() : 0;
            if (!(ms > 0 && ms < now && ms > cutoff.getTime())) return false;
            if (g.status === 'cancelled') return false;
            const hasScore = g.homeScore != null && g.awayScore != null;
            return g.status === 'completed' || hasScore;
          });

        // For each candidate (most-recent first), check whether a
        // broadcast has already been sent for that event. We bail on
        // the first game without a recap — that's the one we surface.
        let nextGame = null;
        for (const g of candidates) {
          try {
            const gameMs = g.date?.toMillis ? g.date.toMillis() : 0;
            const recapQ = query(
              collection(db, 'teams', teamId, 'messages'),
              where('relatedEventId', '==', g.id),
              where('status', '==', 'sent'),
              limit(1),
            );
            const recapSnap = await getDocs(recapQ);
            const alreadyRecapped = recapSnap.docs.some((d) => {
              const sentMs = d.data().sentAt?.toMillis ? d.data().sentAt.toMillis() : 0;
              return sentMs > gameMs;
            });
            if (!alreadyRecapped) {
              nextGame = g;
              break;
            }
          } catch {
            // If the recap-lookup fails (rules / index), fall back to
            // surfacing the prompt rather than silently swallowing it.
            nextGame = g;
            break;
          }
        }
        setRecentGame(nextGame);
      },
      () => setRecentGame(null),
    );
    return unsub;
  }, [teamId]);

  // Upcoming game with no lineup built (for the "build the lineup"
  // prompt). Subscribes to the team's games collection looking for any
  // game in the next 24h, then for each candidate checks the linked
  // attendance session and the corresponding lineups/{sessionId} doc.
  // If there's no lineup or the assignments map is empty, we surface
  // the prompt. Cancelled games are excluded — no point staffing them.
  const [upcomingLineupGap, setUpcomingLineupGap] = useState(null);
  useEffect(() => {
    if (!teamId) return undefined;
    const horizon = new Date(Date.now() + 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, 'games'),
      where('teamId', '==', teamId),
      where('eventType', '==', 'game'),
      orderBy('date', 'asc'),
      limit(5),
    );
    let cancelledFlag = false;
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const now = Date.now();
        const upcoming = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((g) => {
            const ms = g.date?.toMillis ? g.date.toMillis() : 0;
            return ms > now && ms < horizon.getTime() && g.status !== 'cancelled';
          });

        // Walk soonest-first; first game without a lineup wins.
        let gap = null;
        for (const g of upcoming) {
          try {
            // Lineup is keyed by attendance sessionId (see
            // EventDetailPage::getOrCreateSession). If the game has no
            // linked session yet, no lineup can exist either — surface.
            const sessionId = g.attendanceSessionId || null;
            if (!sessionId) {
              gap = { game: g, sessionId: null };
              break;
            }
            const lineupSnap = await getDoc(doc(db, 'lineups', sessionId));
            const data = lineupSnap.exists() ? lineupSnap.data() : null;
            const assigned = data?.assignments
              ? Object.values(data.assignments).filter(Boolean).length
              : 0;
            if (!data || assigned === 0) {
              gap = { game: g, sessionId };
              break;
            }
          } catch {
            // Treat any lineup-lookup error as "no lineup" so the coach
            // still sees the nudge rather than a silent miss.
            gap = { game: g, sessionId: g.attendanceSessionId || null };
            break;
          }
        }
        if (!cancelledFlag) setUpcomingLineupGap(gap);
      },
      () => { if (!cancelledFlag) setUpcomingLineupGap(null); },
    );
    return () => { cancelledFlag = true; unsub(); };
  }, [teamId]);

  // Upcoming events (next 14 days) that have NO usable location for the
  // weather-watch system. "Usable" = either fieldAddress (a string the
  // weather worker can geocode at runtime) OR a fieldId pointing at a
  // saved Fields doc that has lat/lng. If a coach skips Fields setup
  // and types nothing into the address line, weather alerts silently
  // skip the event — this prompt makes that visible. Excludes
  // cancelled events (no point watching weather for a no-go).
  const [eventsWithoutLocation, setEventsWithoutLocation] = useState(0);
  useEffect(() => {
    if (!teamId) return undefined;
    let cancelledFlag = false;
    const horizon = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    const q = query(
      collection(db, 'games'),
      where('teamId', '==', teamId),
      where('eventType', 'in', ['game', 'practice']),
      orderBy('date', 'asc'),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const now = Date.now();
        const upcoming = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((e) => {
            const ms = e.date?.toMillis ? e.date.toMillis() : 0;
            return ms > now && ms < horizon.getTime() && e.status !== 'cancelled';
          });

        // Fast-path partition: if fieldAddress is a non-empty string the
        // weather worker can geocode it directly, so the event is fine.
        // Otherwise we need to check whether its fieldId resolves to a
        // saved Fields doc with lat/lng. We only fetch Fields docs for
        // the events that actually need that lookup — events with no
        // address AND no fieldId are already known-bad.
        const needsFieldLookup = new Map(); // fieldId -> [event, ...]
        let badCount = 0;
        for (const e of upcoming) {
          const addr = (e.fieldAddress || '').trim();
          if (addr) continue; // weather can geocode this
          const fid = e.fieldId;
          if (!fid) { badCount++; continue; } // no address AND no fieldId → unwatchable
          if (!needsFieldLookup.has(fid)) needsFieldLookup.set(fid, []);
          needsFieldLookup.get(fid).push(e);
        }

        // Batch the field-doc lookups: one IN query per ≤10 ids.
        // Fields live at teams/{teamId}/fields/{fieldId} (a subcollection,
        // not a top-level collection — see FieldsPage.jsx).
        if (needsFieldLookup.size > 0) {
          const ids = Array.from(needsFieldLookup.keys());
          const usableIds = new Set();
          try {
            for (let i = 0; i < ids.length; i += 10) {
              const chunk = ids.slice(i, i + 10);
              const fq = query(
                collection(db, 'teams', teamId, 'fields'),
                where(documentId(), 'in', chunk),
              );
              const fsnap = await getDocs(fq);
              fsnap.forEach((d) => {
                const f = d.data();
                if (f?.lat != null && f?.lng != null) usableIds.add(d.id);
              });
            }
          } catch {
            // If the batch lookup fails (rules / index), assume all the
            // pending events are bad rather than silently dropping the
            // prompt — better to over-surface than under-surface.
            for (const events of needsFieldLookup.values()) badCount += events.length;
            if (!cancelledFlag) setEventsWithoutLocation(badCount);
            return;
          }
          for (const [fid, events] of needsFieldLookup.entries()) {
            if (!usableIds.has(fid)) badCount += events.length;
          }
        }

        if (!cancelledFlag) setEventsWithoutLocation(badCount);
      },
      () => { if (!cancelledFlag) setEventsWithoutLocation(0); },
    );
    return () => { cancelledFlag = true; unsub(); };
  }, [teamId]);

  // Disengaged parents on the active-season roster — parents whose user
  // doc presence.lastSeenAt is more than 14 days ago. Drives the
  // "families are quiet" smart action below.
  //
  // Pipeline:
  //   1. Subscribe to active registrations for this season (status in
  //      paid / confirmed) so the roster keeps in sync if a parent is
  //      added or refunded mid-season.
  //   2. Collect distinct parentUids — registrations missing a parentUid
  //      (legacy rows the backfill hasn't reached yet, or parents who
  //      never created a Scrimly account) are silently skipped. Their
  //      data simply doesn't exist for us to check; better to under-
  //      surface than to invent disengaged claims out of nothing.
  //   3. Batch-fetch users/{uid}.presence.lastSeenAt in chunks of 10
  //      (Firestore IN-query limit). Anything older than the 14-day
  //      cutoff lands in the disengaged list. Users with no presence
  //      field at all are also treated as disengaged (they've literally
  //      never opened the app, which is the same signal we care about).
  //   4. Errors anywhere in the chain quietly resolve to an empty list
  //      so a missing index / rules denial never hides the whole smart-
  //      actions strip.
  const [disengagedParents, setDisengagedParents] = useState([]);
  useEffect(() => {
    if (!teamId || !activeSeason?.id) {
      setDisengagedParents([]);
      return undefined;
    }
    let cancelled = false;
    const QUIET_CUTOFF_MS = 14 * 24 * 60 * 60 * 1000;

    const regsQ = query(
      collection(db, 'registrations'),
      where('teamId', '==', teamId),
      where('seasonId', '==', activeSeason.id),
    );
    const unsub = onSnapshot(
      regsQ,
      async (snap) => {
        try {
          // Distinct parentUids from active roster registrations.
          const seen = new Map(); // uid -> displayName fallback
          snap.docs.forEach((d) => {
            const r = d.data();
            const status = String(r.status || '').toLowerCase();
            if (!['paid', 'confirmed'].includes(status)) return;
            if (!r.parentUid) return;
            if (seen.has(r.parentUid)) return;
            seen.set(r.parentUid, r.parentName || null);
          });

          if (seen.size === 0) {
            if (!cancelled) setDisengagedParents([]);
            return;
          }

          const cutoff = Date.now() - QUIET_CUTOFF_MS;
          const uids = Array.from(seen.keys());
          const quiet = [];

          for (let i = 0; i < uids.length; i += 10) {
            const chunk = uids.slice(i, i + 10);
            try {
              const usersQ = query(
                collection(db, 'users'),
                where(documentId(), 'in', chunk),
              );
              const usersSnap = await getDocs(usersQ);
              // Build lookup of present users so we can also detect
              // registrations whose parentUid points at a user doc that
              // doesn't exist yet (rare, but possible if the doc was
              // deleted) — those are treated as "never seen".
              const found = new Set();
              usersSnap.forEach((u) => {
                found.add(u.id);
                const data = u.data() || {};
                const lastSeenMs = data.presence?.lastSeenAt?.toMillis
                  ? data.presence.lastSeenAt.toMillis()
                  : (data.presence?.lastSeenAt?.seconds
                      ? data.presence.lastSeenAt.seconds * 1000
                      : 0);
                if (!lastSeenMs || lastSeenMs < cutoff) {
                  quiet.push({
                    uid: u.id,
                    displayName: data.displayName
                      || [data.firstName, data.lastName].filter(Boolean).join(' ').trim()
                      || seen.get(u.id)
                      || null,
                    lastSeenMs: lastSeenMs || 0,
                  });
                }
              });
              for (const uid of chunk) {
                if (found.has(uid)) continue;
                quiet.push({
                  uid,
                  displayName: seen.get(uid) || null,
                  lastSeenMs: 0,
                });
              }
            } catch {
              // A failed chunk shouldn't drop the whole prompt. Continue
              // with the chunks that succeed; the ones that didn't will
              // just go missing this render cycle.
            }
          }

          // Stable sort: longest-quiet first so the worst offenders show
          // up at the top of the chip list.
          quiet.sort((a, b) => (a.lastSeenMs || 0) - (b.lastSeenMs || 0));

          if (!cancelled) setDisengagedParents(quiet);
        } catch {
          if (!cancelled) setDisengagedParents([]);
        }
      },
      () => { if (!cancelled) setDisengagedParents([]); },
    );
    return () => { cancelled = true; unsub(); };
  }, [teamId, activeSeason?.id]);

  const actions = useMemo(() => {
    const out = [];

    // Active-season-dependent prompts (closing reg, unpaid, missing
    // docs) ALL gate on activeSeason existing. Match Recap doesn't —
    // it can fire on an archived season's last game or even after
    // the season closes. So the early return is gated below the
    // first three branches but BEFORE the recap branch.
    if (!activeSeason) {
      // No active season — fall through to recap-only check below.
      // (We don't `return out` here.)
    }

    // 1. Registration closing soon (≤7 days, not yet closed)
    if (activeSeason && daysUntilClose !== null && daysUntilClose <= 7 && daysUntilClose > 0) {
      const urgent = daysUntilClose <= 2;
      const spotsLeft = activeSeason.maxPlayers
        ? activeSeason.maxPlayers - (activeRegCount ?? 0)
        : null;
      out.push({
        id: 'closing',
        icon: <CalendarDaysIcon className={clsx('h-4 w-4', urgent ? 'text-red-600' : 'text-amber-600')} />,
        iconBg: urgent ? 'bg-red-100' : 'bg-amber-100',
        title: t('dashboard.smartActions.closing', { count: daysUntilClose }),
        sub: spotsLeft !== null
          ? t('dashboard.smartActions.spotsLeft', { registered: activeRegCount ?? 0, spots: spotsLeft })
          : t('dashboard.smartActions.justRegistered', { registered: activeRegCount ?? 0 }),
        titleColor: urgent ? 'text-red-700' : 'text-amber-700',
        borderColor: urgent ? 'border-red-200' : 'border-amber-200',
        bgTint: urgent ? 'bg-red-50' : 'bg-amber-50',
        ctaBg: 'bg-[#b5f03d] text-[#0d1f14] hover:bg-[#94cc28]',
        ctaLabel: t('dashboard.smartActions.sendReminder'),
        ctaTo: teamId ? `/teams/${teamId}/messages/new?type=${encodeURIComponent('Reminder')}` : null,
      });
    }

    // 2. Lineup not set for an upcoming game (≤24h away). Game-day
    // logistics outrank financial follow-ups: a missing lineup at
    // kickoff is a coaching emergency, an unpaid invoice can wait a
    // day. Doesn't gate on activeSeason — a tournament game on a
    // closed season still needs a starting XI.
    if (upcomingLineupGap?.game) {
      const g = upcomingLineupGap.game;
      const gameTime = g.date?.toDate
        ? g.date.toDate()
        : (g.date?.seconds ? new Date(g.date.seconds * 1000) : null);
      const hoursUntil = gameTime
        ? Math.max(1, Math.round((gameTime.getTime() - Date.now()) / 3_600_000))
        : null;
      const opp = g.opponent || null;
      const dateStr = gameTime
        ? gameTime.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
        : null;
      const urgent = hoursUntil !== null && hoursUntil <= 6;
      // Route to the lineup builder. If we have a sessionId we can deep-
      // link straight in; otherwise fall back to the event detail page,
      // which auto-creates the session via getOrCreateSession.
      const lineupTo = (upcomingLineupGap.sessionId && activeSeason?.id)
        ? `/seasons/${activeSeason.id}/lineup/${upcomingLineupGap.sessionId}`
        : (g.seasonId
            ? `/seasons/${g.seasonId}/event/${g.id}`
            : null);
      out.push({
        id: 'lineupGap',
        icon: <ClipboardDocumentListIcon className={clsx('h-4 w-4', urgent ? 'text-red-600' : 'text-[#5a8a1f]')} />,
        iconBg: urgent ? 'bg-red-100' : 'bg-[#b5f03d]/20',
        title: <>Game in {hoursUntil ? `${hoursUntil}h` : 'under a day'} — build the lineup?</>,
        sub: opp
          ? <>vs {opp}{dateStr ? ` · ${dateStr}` : ''}</>
          : <>game{dateStr ? ` on ${dateStr}` : ''}</>,
        titleColor: urgent ? 'text-red-700' : 'text-[#0d1f14]',
        borderColor: urgent ? 'border-red-200' : 'border-[#b5f03d]/40',
        bgTint: urgent ? 'bg-red-50' : 'bg-[#b5f03d]/10',
        ctaBg: 'bg-[#b5f03d] text-[#0d1f14] hover:bg-[#94cc28]',
        ctaLabel: 'Build lineup',
        ctaTo: lineupTo,
      });
    }

    // 3. Events without field locations — weather watch can't fire on
    // an event with neither a fieldAddress nor a geocoded saved Field.
    // Sits between lineup-due and payment prompts: more important than
    // chasing money (silent weather miss has safety implications) but
    // less urgent than a missing kickoff lineup. Lime/pitch palette,
    // not red — informational, not critical.
    if (eventsWithoutLocation > 0) {
      const n = eventsWithoutLocation;
      const title = n === 1
        ? <>1 upcoming event has no location — weather can&apos;t watch it</>
        : <>{n} upcoming events have no location — weather watch is off for them</>;
      out.push({
        id: 'noLocation',
        icon: <MapPinIcon className="h-4 w-4 text-[#5a8a1f]" />,
        iconBg: 'bg-[#b5f03d]/20',
        title,
        sub: <>add fields so weather alerts cover every session</>,
        titleColor: 'text-[#0d1f14]',
        borderColor: 'border-[#b5f03d]/40',
        bgTint: 'bg-[#b5f03d]/10',
        ctaBg: 'bg-[#b5f03d] text-[#0d1f14] hover:bg-[#94cc28]',
        ctaLabel: 'Add field locations',
        ctaTo: teamId ? `/teams/${teamId}/fields` : null,
      });
    }

    // 4. Unpaid registrations
    if (activeSeason && unpaidCount > 0) {
      out.push({
        id: 'unpaid',
        icon: <CurrencyDollarIcon className="h-4 w-4 text-orange-600" />,
        iconBg: 'bg-orange-100',
        title: t('dashboard.smartActions.unpaid', { count: unpaidCount }),
        sub: t('dashboard.smartActions.paymentPending', { season: activeSeason.name }),
        titleColor: 'text-orange-700',
        borderColor: 'border-orange-200',
        bgTint: 'bg-orange-50',
        ctaBg: 'bg-[#b5f03d] text-[#0d1f14] hover:bg-[#94cc28]',
        ctaLabel: t('dashboard.smartActions.generateReminder'),
        ctaTo: teamId ? `/teams/${teamId}/messages/new?type=${encodeURIComponent('Payment Due')}` : null,
      });
    }

    // 5. Missing required documents
    if (activeSeason && missingDocsCount > 0) {
      out.push({
        id: 'missingDocs',
        icon: <DocumentCheckIcon className="h-4 w-4 text-violet-600" />,
        iconBg: 'bg-violet-100',
        title: t('dashboard.smartActions.missingDocs', { count: missingDocsCount }),
        sub: activeSeason.name,
        titleColor: 'text-violet-700',
        borderColor: 'border-violet-200',
        bgTint: 'bg-violet-50',
        ctaBg: 'bg-[#b5f03d] text-[#0d1f14] hover:bg-[#94cc28]',
        ctaLabel: t('dashboard.smartActions.viewAndRemind'),
        ctaTo: `/seasons/${activeSeason.id}/registrations`,
      });
    }

    // 6. Match Recap due — fires when a played game in the last 5 days
    // has a recorded score and no broadcast tagged with relatedEventId
    // has gone out for it. The query in the recentGame effect already
    // filters out cancelled / unscored games and games that have a
    // sent recap, so this branch just renders. Copy is day-aware:
    // ≤48h leans celebratory ("share the W"), older shifts to nudgey
    // ("before momentum fades"). Loss/draw uses neutral "result" copy.
    // Lowest priority of the action stack — momentum-fading nudges
    // shouldn't outrank docs/payments still in flight.
    if (recentGame) {
      const opp = recentGame.opponent || null;
      const gameTime = recentGame.date?.toDate
        ? recentGame.date.toDate()
        : (recentGame.date?.seconds ? new Date(recentGame.date.seconds * 1000) : null);
      const msAgo = gameTime ? (Date.now() - gameTime.getTime()) : 0;
      const hoursAgo = gameTime ? Math.max(1, Math.round(msAgo / 3_600_000)) : null;
      const daysAgo = gameTime ? Math.max(1, Math.round(msAgo / (24 * 3_600_000))) : null;
      const isFresh = msAgo > 0 && msAgo <= 48 * 3_600_000;
      // "W" only if we know we won — fall back to neutral "result" for
      // ties, losses, or missing scores so we don't celebrate a loss.
      const home = recentGame.homeScore;
      const away = recentGame.awayScore;
      const isHome = recentGame.isHome !== false; // default-on; matches existing schedule defaults
      const won = (home != null && away != null)
        ? (isHome ? home > away : away > home)
        : false;
      let title;
      if (isFresh) {
        title = won
          ? <>Score recorded — share the W with the families?</>
          : <>Score recorded — share the result with the families?</>;
      } else {
        title = opp
          ? <>It&apos;s been {daysAgo}d since vs {opp} — share a recap before momentum fades?</>
          : <>It&apos;s been {daysAgo}d since the last game — share a recap before momentum fades?</>;
      }
      out.push({
        id: 'recap',
        icon: <SparklesIcon className="h-4 w-4 text-[#5a8a1f]" />,
        iconBg: 'bg-[#b5f03d]/20',
        title,
        sub: opp
          ? <>vs {opp}{hoursAgo ? ` · ${hoursAgo}h ago` : ''}</>
          : <>let parents know how it went</>,
        titleColor: 'text-[#0d1f14]',
        borderColor: 'border-[#b5f03d]/40',
        bgTint: 'bg-[#b5f03d]/10',
        ctaBg: 'bg-[#b5f03d] text-[#0d1f14] hover:bg-[#94cc28]',
        ctaLabel: 'Share recap',
        ctaTo: teamId
          ? `/teams/${teamId}/messages/new?type=${encodeURIComponent('Game Recap')}&relatedEventId=${encodeURIComponent(recentGame.id)}`
          : null,
      });
    }

    // 7. Disengaged parents — lowest priority, informational nudge.
    //
    // Driven by the disengagedParents state above (built by walking
    // active-roster registrations.parentUid → users/{uid}.presence
    // .lastSeenAt). Parents quiet for 2+ weeks land here. The CTA
    // routes to the message composer with `audience=disengaged`, a
    // contract param ComposerWizard can consume to pre-filter the
    // recipient list (similar to the audience=unpaid pattern). Until
    // that's wired the audience picker falls back to the manual list,
    // so the nudge still works.
    const quiet = disengagedParents;
    if (quiet && quiet.length > 0) {
      const names = quiet.slice(0, 5).map((p) => p.displayName || 'a parent');
      let title;
      let sub;
      if (quiet.length === 1) {
        const p = quiet[0];
        const days = p.lastSeenMs
          ? Math.max(1, Math.round((Date.now() - p.lastSeenMs) / (24 * 3_600_000)))
          : null;
        title = days
          ? <>{p.displayName || 'A parent'} hasn&apos;t opened Scrimly in {days} days — DM them?</>
          : <>{p.displayName || 'A parent'} hasn&apos;t opened Scrimly yet — DM them?</>;
        sub = <>quiet for 2+ weeks</>;
      } else if (quiet.length <= 5) {
        title = <>{quiet.length} families haven&apos;t opened Scrimly in 2+ weeks — check in?</>;
        sub = <>{names.join(', ')}</>;
      } else {
        title = <>{quiet.length} families are quiet</>;
        sub = <>first 5: {names.join(', ')}, and {quiet.length - 5} more</>;
      }
      out.push({
        id: 'disengaged',
        icon: <UsersIcon className="h-4 w-4 text-zinc-500" />,
        iconBg: 'bg-zinc-100',
        title,
        sub,
        titleColor: 'text-zinc-700',
        borderColor: 'border-zinc-200',
        bgTint: 'bg-zinc-50',
        ctaBg: 'bg-[#b5f03d] text-[#0d1f14] hover:bg-[#94cc28]',
        ctaLabel: 'Send a check-in',
        ctaTo: teamId
          ? `/teams/${teamId}/messages/new?type=${encodeURIComponent('Reminder')}&audience=disengaged`
          : null,
      });
    }

    return out;
  }, [activeSeason, daysUntilClose, activeRegCount, unpaidCount, missingDocsCount, teamId, t, recentGame, upcomingLineupGap, eventsWithoutLocation, disengagedParents]);

  // RecommendToDirectorCard self-hides when not eligible (returns null),
  // so it's safe to render unconditionally. It has its own internal
  // eligibility logic (team-owner + no clubId + ≥5 regs + not dismissed/
  // submitted) and its own card styling, so when shown it stands alone
  // without needing the panel header.
  // (Part of features/recommend-to-director/.)
  const recommendCard = teamId ? <RecommendToDirectorCard teamId={teamId} /> : null;

  if (actions.length === 0) {
    // No standard smart-actions to show, but the recommend card may
    // still render below. Return just the card (or nothing if it's
    // also self-hidden).
    return recommendCard ? <div className="mb-5">{recommendCard}</div> : null;
  }

  return (
    <div className="mb-5">
      <p
        className="mb-2.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
        style={{ fontFamily: HEAD_FONT }}
      >
        <BellAlertIcon className="h-3.5 w-3.5" />
        {t('dashboard.smartActions.title')}
      </p>
      <div className="flex flex-col gap-2.5">
        {actions.map((action) => (
          <div
            key={action.id}
            className={clsx(
              'flex items-center gap-3 rounded-xl border px-3.5 py-3 min-w-0',
              action.borderColor, action.bgTint,
            )}
          >
            <div className={clsx('flex-shrink-0 rounded-lg p-2', action.iconBg)}>
              {action.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className={clsx('text-xs font-bold leading-snug', action.titleColor)}>{action.title}</p>
              <p className="mt-0.5 text-[11px] text-zinc-500 truncate">{action.sub}</p>
            </div>
            {action.ctaTo && (
              <Link
                to={action.ctaTo}
                className={clsx(
                  'shrink-0 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors',
                  action.ctaBg,
                )}
              >
                {action.ctaLabel}
                <ArrowRightIcon className="h-3 w-3" />
              </Link>
            )}
          </div>
        ))}
        {recommendCard}
      </div>
    </div>
  );
}
